"use client"

import { useState, useEffect } from "react"
import Button from "../modules/Button/Button"
import Modal from "../modules/Modal/Moadal"
import Select from "../modules/Select/Select"
import Input from "../modules/Input/Input"
import CollapsibleSection from "../../collapsableSection"
import DatePicker from "../../shared-modules/DatePicker/DatePicker"
import styles from "../../../styles/inventory/MoveSupplyForm.module.css"

// 🔹 Account options (same as devices)
const getAccountOptions = () => [
  { value: "rebul-consultant", label: "Rebul Consultant" },
  { value: "healthcare-partners", label: "Healthcare Partners" },
  { value: "medical-solutions", label: "Medical Solutions" },
  { value: "wellness-group", label: "Wellness Group" },
]

const getLocationsByAccount = (accountValue) => {
  const locationMap = {
    "rebul-consultant": [
      { value: "dialysis-center", label: "Dialysis Center" },
      { value: "granada", label: "Granada" },
      { value: "warehouse", label: "Warehouse" },
    ],
    "healthcare-partners": [
      { value: "main-clinic", label: "Main Clinic" },
      { value: "north-branch", label: "North Branch" },
      { value: "south-branch", label: "South Branch" },
    ],
    "medical-solutions": [
      { value: "central-hub", label: "Central Hub" },
      { value: "east-facility", label: "East Facility" },
      { value: "west-facility", label: "West Facility" },
    ],
    "wellness-group": [
      { value: "primary-location", label: "Primary Location" },
      { value: "secondary-location", label: "Secondary Location" },
    ],
  }
  return locationMap[accountValue] || []
}

const getAccountByLocation = (locationValue) => {
  const accountMap = {
    "dialysis-center": "rebul-consultant",
    granada: "rebul-consultant",
    warehouse: "rebul-consultant",
    "main-clinic": "healthcare-partners",
    "north-branch": "healthcare-partners",
    "south-branch": "healthcare-partners",
    "central-hub": "medical-solutions",
    "east-facility": "medical-solutions",
    "west-facility": "medical-solutions",
    "primary-location": "wellness-group",
    "secondary-location": "wellness-group",
  }
  return accountMap[locationValue] || ""
}

const getAllLocations = () => {
  const accounts = getAccountOptions()
  const allLocations = []
  accounts.forEach((account) => {
    const locations = getLocationsByAccount(account.value)
    allLocations.push(...locations)
  })
  return allLocations
}

const getOrderOptions = () => [
  { value: "None", label: "None" },
  { value: "ORD-001", label: "ORD-001" },
  { value: "ORD-002", label: "ORD-002" },
  { value: "ORD-003", label: "ORD-003" },
  { value: "ORD-004", label: "ORD-004" },
]

const getLocationLabel = (value, options) => {
  return options.find((opt) => opt.value === value)?.label || ""
}

const getAccountLabel = (value, options) => {
  return options.find((opt) => opt.value === value)?.label || ""
}

const initializeCollapsedState = (items) => {
  const collapsed = {}
  items.forEach((item) => {
    collapsed[item.id] = true
  })
  return collapsed
}

const MoveSupplyForm = ({ supplies = [], isBulkMove, isOpen, onClose }) => {
  const [selectedSupplies, setSelectedSupplies] = useState([])
  const [quantityInputs, setQuantityInputs] = useState({})
  const [formData, setFormData] = useState({
    caseManager: "",
    moveFromLocation: "",
    moveToAccount: "",
    moveToLocation: "",
    moveDate: "",
  })
  const [collapsedItems, setCollapsedItems] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedOrderId, setSelectedOrderId] = useState("")

  const accountOptions = getAccountOptions()
  const allLocations = getAllLocations()
  const availableLocations = formData.moveToAccount ? getLocationsByAccount(formData.moveToAccount) : allLocations

  useEffect(() => {
    if (supplies && supplies.length > 0) {
      setSelectedSupplies(supplies)
      setFormData((prev) => ({
        ...prev,
        caseManager: supplies[0]?.caseManager || "",
        moveFromLocation: supplies[0]?.location || "",
      }))
      setCollapsedItems(initializeCollapsedState(supplies))

      // set default quantity = available quantity
      const initialQuantities = {}
      supplies.forEach((s) => {
        initialQuantities[s.id] = s.quantity || 0
      })
      setQuantityInputs(initialQuantities)
    }
  }, [supplies])

  useEffect(() => {
    const valid =
      formData.caseManager &&
      formData.moveToLocation &&
      formData.moveDate &&
      selectedSupplies.length > 0 &&
      Object.values(quantityInputs).some((q) => q > 0)

    setIsFormValid(valid)
  }, [formData, selectedSupplies, quantityInputs])

  const handleInputChange = (name, value) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: value }

      if (name === "moveToAccount") {
        if (prev.moveToLocation) {
          const locationAccount = getAccountByLocation(prev.moveToLocation)
          if (locationAccount !== value) {
            newFormData.moveToLocation = ""
          }
        }
      }

      if (name === "moveToLocation") {
        const locationAccount = getAccountByLocation(value)
        if (locationAccount && locationAccount !== prev.moveToAccount) {
          newFormData.moveToAccount = locationAccount
        }
      }

      return newFormData
    })
  }

  const handleQuantityChange = (supplyId, value, maxQty) => {
    let qty = parseInt(value, 10)
    if (isNaN(qty) || qty < 0) qty = 0
    if (qty > maxQty) qty = maxQty
    setQuantityInputs((prev) => ({ ...prev, [supplyId]: qty }))
  }

  const handleRemoveSupply = (supplyId) => {
    setSelectedSupplies((prev) => prev.filter((supply) => supply.id !== supplyId))
    setCollapsedItems((prev) => {
      const newCollapsed = { ...prev }
      delete newCollapsed[supplyId]
      return newCollapsed
    })
    setQuantityInputs((prev) => {
      const newQuantities = { ...prev }
      delete newQuantities[supplyId]
      return newQuantities
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!isFormValid) return

    const moveData = {
      supplies: selectedSupplies.map((s) => ({
        ...s,
        moveQuantity: quantityInputs[s.id] || 0,
      })),
      caseManager: formData.caseManager,
      moveFromLocation: formData.moveFromLocation,
      moveToAccount: formData.moveToAccount,
      moveToLocation: formData.moveToLocation,
      moveDate: formData.moveDate,
    }

    const accountLabel = getAccountLabel(formData.moveToAccount, accountOptions)
    const toLocationLabel = getLocationLabel(formData.moveToLocation, availableLocations)

    alert(
      `Successfully moved ${selectedSupplies.length} supply item(s) with quantities to ${accountLabel} - ${toLocationLabel}`
    )
    onClose()
  }

  const modalFooter = (
    <div className={styles.button_container}>
      <Button type="button" variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button type="button" variant="primary" disabled={!isFormValid} onClick={handleSubmit}>
        Move {selectedSupplies.length} Item{selectedSupplies.length !== 1 ? "s" : ""}
      </Button>
    </div>
  )

  if (selectedSupplies.length === 0) {
    return (
      <Modal title="Move Supplies" isOpen={isOpen} onClose={onClose} width="900px" height="85vh">
        <div className={styles.move_supply_form} data-move-supply-form>
          <div className={styles.empty_state}>
            <p>No supplies selected for moving.</p>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Move Supplies" isOpen={isOpen} onClose={onClose} width="800px" height="600px" footer={modalFooter}>
      <div className={styles.move_supply_form} data-move-supply-form>
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Top form fields */}
          <div className={styles.location_row}>
            <div className={styles.form_group}>
              <Input
                label="Case Manager"
                type="text"
                name="caseManager"
                value={formData.caseManager}
                onChange={(e) => handleInputChange("caseManager", e.target.value)}
                placeholder="Dan Oswald"
                required
                fullWidth
              />
            </div>

            <div className={styles.form_group}>
              <label className={styles.label}>Move Date</label>
              <DatePicker
                selectedDate={selectedDate}
                onDateSelect={(date) => {
                  setSelectedDate(date)
                  handleInputChange("moveDate", date.toISOString().split("T")[0])
                }}
                placeholder="Select move date"
              />
            </div>
          </div>

          <div className={styles.location_row}>
            <div className={styles.form_group}>
              <Input
                label="Move from Location"
                type="text"
                name="moveFromLocation"
                value={formData.moveFromLocation}
                placeholder="Granada"
                required
                fullWidth
                readOnly
              />
            </div>

            {/* <div className={styles.form_group}>
              <label className={styles.label}>Orders (Optional)</label>
              <Select
                label="Orders"
                value={selectedOrderId}
                onChange={setSelectedOrderId}
                options={getOrderOptions()}
                placeholder="Select Order ID"
                width="100%"
              />
            </div> */}
              <div className={styles.form_group}>
              <label className={styles.label}>Move to Account</label>
              <Select
                label="Move to Account"
                value={formData.moveToAccount}
                onChange={(value) => handleInputChange("moveToAccount", value)}
                options={accountOptions}
                placeholder="Select Account"
                width="100%"
                required
              />
            </div>
          </div>

          <div className={styles.location_row}>
          

            <div className={styles.form_group}>
              <label className={styles.label}>Move to Location</label>
              <Select
                label="Move to Location"
                value={formData.moveToLocation}
                onChange={(value) => handleInputChange("moveToLocation", value)}
                options={availableLocations}
                placeholder="Select Destination Location"
                width="100%"
                required
                disabled={!formData.moveToAccount}
              />
            </div>
          </div>

          {/* Summary */}
          <div className={styles.summary_section}>
            <div className={styles.summary_item}>
              <span className={styles.summary_label}>Total Supplies:</span>
              <span className={styles.summary_value}>{selectedSupplies.length}</span>
            </div>
          </div>

          {/* Selected supplies list */}
          <div className={styles.selected_items}>
            {selectedSupplies.map((supply) => (
              <CollapsibleSection
                key={supply.id}
                title={`${supply.itemName} - ${supply.typeOfSupply}`}
                isOpen={!collapsedItems[supply.id]}
                onClose={() =>
                  setCollapsedItems((prev) => ({ ...prev, [supply.id]: !prev[supply.id] }))
                }
                bodyHeight="auto"
                actions={[
                  <button
                    key="remove"
                    className={styles.remove_button}
                    onClick={() => handleRemoveSupply(supply.id)}
                    title="Remove Supply"
                    type="button"
                  >
                    ×
                  </button>,
                ]}
                content={
                  <div className={styles.card_content}>
                     <div className={styles.card_field}>
                        <span className={styles.field_label}>Item Name</span>
                        <span className={styles.field_value}>{supply.itemName}</span>
                      </div>
                    <div className={styles.card_row}>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Case Manager</span>
                        <span className={styles.field_value}>{supply.caseManager}</span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Type</span>
                        <span className={styles.field_value}>{supply.typeOfSupply}</span>
                      </div>
                     
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Available Quantity</span>
                        <span className={styles.field_value}>{supply.quantity}</span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Move Quantity</span>
                        <Input
                          type="number"
                          min="0"
                          max={supply.quantity}
                          value={quantityInputs[supply.id] || ""}
                          onChange={(e) =>
                            handleQuantityChange(supply.id, e.target.value, supply.quantity)
                          }
                          placeholder="Enter quantity"
                        />
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Location</span>
                        <span className={styles.field_value}>{supply.location}</span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Date</span>
                        <span className={styles.field_value}>{supply.date}</span>
                      </div>
                      <div className={styles.card_field}>
                        <span className={styles.field_label}>Status</span>
                        <span className={styles.field_value}>{supply.status}</span>
                      </div>
                    </div>
                  </div>
                }
              />
            ))}
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default MoveSupplyForm
